import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { color } from '@cwos/ui-shared';
import { GeoMap } from '../map/GeoMap.js';
import { loadGoogleMaps } from '../map/googleMapsLoader.js';
import { resolveMapsKey } from '../map/googleMapsLogic.js';
import { composeGeocodeQuery, coordinateProvenanceLabel, formatCoordinate, geocodeFeedback, nudge, parseLatitude, parseLongitude, } from './sitePickerLogic.js';
import styles from './settings.module.css';
const NUDGE_METERS = 10;
const DEFAULT_HEIGHT = 320;
export function SiteMapPicker({ address, latitude, longitude, coordinateSource, coordinateSetAt, geofenceRadiusMeters, disabled, mapsApiKey, height = DEFAULT_HEIGHT, onChange, onClear, }) {
    const key = resolveMapsKey({ VITE_GOOGLE_MAPS_API_KEY: mapsApiKey });
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const circleRef = useRef(null);
    const geocoderRef = useRef(null);
    const [mapReady, setMapReady] = useState(false);
    const [mapsFailed, setMapsFailed] = useState(false);
    const [lookingUp, setLookingUp] = useState(false);
    const [outcome, setOutcome] = useState(null);
    const [latText, setLatText] = useState(latitude == null ? '' : formatCoordinate(latitude));
    const [lngText, setLngText] = useState(longitude == null ? '' : formatCoordinate(longitude));
    // Keep the latest onChange without making it an effect dependency (it is
    // recreated on every parent render).
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    useEffect(() => {
        setLatText(latitude == null ? '' : formatCoordinate(latitude));
        setLngText(longitude == null ? '' : formatCoordinate(longitude));
    }, [latitude, longitude]);
    const position = latitude != null && longitude != null ? { latitude, longitude } : null;
    const query = composeGeocodeQuery(address);
    const mapsAvailable = key != null && !mapsFailed;
    // --- Google Maps layer ----------------------------------------------------
    useEffect(() => {
        if (!key)
            return;
        let cancelled = false;
        loadGoogleMaps(key)
            .then(() => {
            if (cancelled || !containerRef.current)
                return;
            const map = new google.maps.Map(containerRef.current, {
                center: position
                    ? { lat: position.latitude, lng: position.longitude }
                    : { lat: 39.5, lng: -98.35 },
                zoom: position ? 16 : 4,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                gestureHandling: 'cooperative',
            });
            mapRef.current = map;
            geocoderRef.current = new google.maps.Geocoder();
            setMapReady(true);
        })
            .catch(() => {
            if (!cancelled)
                setMapsFailed(true);
        });
        return () => {
            cancelled = true;
        };
        // Only the key drives (re)initialisation. `position` is read here purely to
        // pick an opening centre; keeping it out of the dependency list is
        // deliberate, because re-creating the map on every coordinate change would
        // destroy the marker the person is currently dragging. The effect below
        // keeps the map in step with the position instead.
    }, [key]);
    // Marker + geofence circle follow the current position.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapReady)
            return;
        if (!position) {
            markerRef.current?.setMap(null);
            markerRef.current = null;
            circleRef.current?.setMap(null);
            circleRef.current = null;
            return;
        }
        const point = { lat: position.latitude, lng: position.longitude };
        if (!markerRef.current) {
            const marker = new google.maps.Marker({
                position: point,
                map,
                draggable: !disabled,
                title: 'Site position — drag to adjust',
            });
            marker.addListener('dragend', () => {
                const p = marker.getPosition();
                if (!p)
                    return;
                // A dragged pin is a human decision, so its provenance is 'manual' and
                // any address the geocoder had resolved no longer describes it.
                onChangeRef.current({
                    latitude: p.lat(),
                    longitude: p.lng(),
                    source: 'manual',
                    resolvedAddress: null,
                });
            });
            markerRef.current = marker;
        }
        else {
            markerRef.current.setPosition(point);
            markerRef.current.setDraggable(!disabled);
        }
        if (geofenceRadiusMeters && geofenceRadiusMeters > 0) {
            if (!circleRef.current) {
                circleRef.current = new google.maps.Circle({
                    map,
                    center: point,
                    radius: geofenceRadiusMeters,
                    fillColor: color.sky,
                    fillOpacity: 0.18,
                    strokeColor: color.skyDeep,
                    strokeWeight: 1,
                    clickable: false,
                });
            }
            else {
                circleRef.current.setCenter(point);
                circleRef.current.setRadius(geofenceRadiusMeters);
            }
        }
        else {
            circleRef.current?.setMap(null);
            circleRef.current = null;
        }
        map.panTo(point);
    }, [mapReady, position?.latitude, position?.longitude, geofenceRadiusMeters, disabled]);
    // --- Actions --------------------------------------------------------------
    function lookUpAddress() {
        if (!query)
            return;
        const geocoder = geocoderRef.current;
        if (!geocoder) {
            setOutcome({ kind: 'denied', count: 0 });
            return;
        }
        setLookingUp(true);
        setOutcome(null);
        // The ONLY payload: the address string. Nothing else about this site, and
        // nothing at all about clients or services, is in scope here.
        geocoder.geocode({ address: query }, (results, status) => {
            setLookingUp(false);
            if (status !== google.maps.GeocoderStatus.OK || !results || results.length === 0) {
                setOutcome({
                    kind: status === google.maps.GeocoderStatus.ZERO_RESULTS
                        ? 'not_found'
                        : status === google.maps.GeocoderStatus.REQUEST_DENIED
                            ? 'denied'
                            : 'failed',
                    count: 0,
                });
                // Deliberately no onChange: a failed lookup leaves the position alone
                // rather than inventing one.
                return;
            }
            const best = results[0];
            const loc = best.geometry.location;
            setOutcome({ kind: results.length > 1 ? 'ambiguous' : 'ok', count: results.length });
            onChangeRef.current({
                latitude: loc.lat(),
                longitude: loc.lng(),
                source: 'geocoded',
                resolvedAddress: best.formatted_address ?? null,
            });
        });
    }
    function commitTypedCoordinates() {
        const lat = parseLatitude(latText);
        const lng = parseLongitude(lngText);
        if (lat == null || lng == null)
            return;
        if (lat === latitude && lng === longitude)
            return;
        onChangeRef.current({ latitude: lat, longitude: lng, source: 'manual', resolvedAddress: null });
    }
    function move(direction) {
        if (!position)
            return;
        const next = nudge(position, direction, NUDGE_METERS);
        onChangeRef.current({
            latitude: next.latitude,
            longitude: next.longitude,
            source: 'manual',
            resolvedAddress: null,
        });
    }
    const typedLat = parseLatitude(latText);
    const typedLng = parseLongitude(lngText);
    const latInvalid = latText.trim() !== '' && typedLat == null;
    const lngInvalid = lngText.trim() !== '' && typedLng == null;
    const feedback = outcome ? geocodeFeedback(outcome.kind, outcome.count) : null;
    return (_jsxs("div", { className: styles.picker, children: [_jsxs("div", { className: styles.pickerActions, children: [_jsx("button", { type: "button", className: styles.primaryBtn, disabled: disabled || !query || !mapsAvailable || lookingUp, onClick: lookUpAddress, children: lookingUp ? 'Looking up…' : 'Find address on map' }), !query ? (_jsx("span", { className: styles.hint, children: "Enter a street, city or postcode to look it up." })) : !mapsAvailable ? (_jsx("span", { className: styles.hint, children: "Address lookup isn\u2019t available in this environment. Set the position by hand below." })) : null] }), feedback ? (_jsx("p", { className: styles.pickerFeedback, "data-tone": feedback.tone, role: "status", "aria-live": "polite", children: feedback.message })) : null, mapsAvailable ? (_jsx("div", { ref: containerRef, className: styles.pickerMap, style: { height }, role: "application", "aria-label": "Site position map. The marker can be dragged; the coordinate fields and nudge buttons below do the same thing without a mouse." })) : (_jsxs("div", { className: styles.pickerFallback, children: [_jsx("p", { className: styles.hint, children: "Showing a simple position preview \u2014 this is not Google Maps, and it cannot be panned or searched." }), _jsx(GeoMap, { height: height, fences: position && geofenceRadiusMeters
                            ? [
                                {
                                    id: 'site',
                                    name: 'Geofence',
                                    latitude: position.latitude,
                                    longitude: position.longitude,
                                    radiusMeters: geofenceRadiusMeters,
                                },
                            ]
                            : [], markers: position
                            ? [
                                {
                                    id: 'site',
                                    latitude: position.latitude,
                                    longitude: position.longitude,
                                    kind: 'stop',
                                    label: 'Site',
                                },
                            ]
                            : [], emptyMessage: "No position set yet." })] })), _jsxs("div", { className: styles.pickerCoords, children: [_jsxs("label", { className: styles.field, children: [_jsx("span", { className: styles.label, children: "Latitude" }), _jsx("input", { className: styles.input, inputMode: "decimal", value: latText, disabled: disabled, "aria-invalid": latInvalid || undefined, "aria-describedby": latInvalid ? 'site-lat-error' : undefined, onChange: (e) => setLatText(e.target.value), onBlur: commitTypedCoordinates }), latInvalid ? (_jsx("span", { id: "site-lat-error", className: styles.pickerError, children: "Latitude must be between \u221290 and 90." })) : null] }), _jsxs("label", { className: styles.field, children: [_jsx("span", { className: styles.label, children: "Longitude" }), _jsx("input", { className: styles.input, inputMode: "decimal", value: lngText, disabled: disabled, "aria-invalid": lngInvalid || undefined, "aria-describedby": lngInvalid ? 'site-lng-error' : undefined, onChange: (e) => setLngText(e.target.value), onBlur: commitTypedCoordinates }), lngInvalid ? (_jsx("span", { id: "site-lng-error", className: styles.pickerError, children: "Longitude must be between \u2212180 and 180." })) : null] })] }), _jsx("div", { className: styles.pickerNudge, role: "group", "aria-label": "Move the site position by 10 metres", children: ['north', 'south', 'east', 'west'].map((direction) => (_jsx("button", { type: "button", className: styles.quietBtn, disabled: disabled || !position, onClick: () => move(direction), children: direction === 'north'
                        ? '↑ 10 m north'
                        : direction === 'south'
                            ? '↓ 10 m south'
                            : direction === 'east'
                                ? '→ 10 m east'
                                : '← 10 m west' }, direction))) }), _jsxs("p", { className: styles.hint, children: [position
                        ? coordinateProvenanceLabel(coordinateSource, coordinateSetAt)
                        : 'No position set — a site can be saved without one, but it cannot have a geofence.', position && onClear && !disabled ? (_jsxs(_Fragment, { children: [' · ', _jsx("button", { type: "button", className: styles.linkBtn, onClick: onClear, children: "Clear position" })] })) : null] })] }));
}
//# sourceMappingURL=SiteMapPicker.js.map